import { useRef, useState } from "react";
import type { FocusEvent, ReactElement } from "react";
import { EditOutlined } from "@ant-design/icons";
import { Button, InputNumber, message } from "antd";
import { changeGoodsSort } from "@/api/shopGoods";
import type { GoodsRow } from "@/types/shopGoods";

interface GoodsSortInputProps {
  row: GoodsRow;
  disabled: boolean;
  onSaved: () => void;
}

export function GoodsSortInput({ row, disabled, onSaved }: GoodsSortInputProps): ReactElement {
  const [sort, setSort] = useState<number | null>(row.sort);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const submitting = useRef(false);

  const handleEdit = (): void => {
    setSort(row.sort);
    setEditing(true);
  };

  const handleBlur = async (event: FocusEvent<HTMLInputElement>): Promise<void> => {
    if (disabled || submitting.current) return;
    const input = event.target.value.trim();
    const value = Number(input);
    if (!input || !Number.isInteger(value) || value < 0 || value > 2147483647) {
      void message.warning("商品排序必须为 0 至 2147483647 的整数");
      setSort(row.sort);
      setEditing(false);
      return;
    }
    if (value === row.sort) {
      setEditing(false);
      return;
    }
    submitting.current = true;
    setSaving(true);
    try {
      await changeGoodsSort(row.id, value);
      void message.success("商品排序已更新");
      setEditing(false);
      onSaved();
    } catch {
      // 请求层统一提示错误，恢复已保存的排序值。
      setSort(row.sort);
      setEditing(false);
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  };

  return editing ? (
    <InputNumber<number>
      autoFocus
      size="small"
      style={{ width: 100 }}
      value={sort}
      min={0}
      max={2147483647}
      step={1}
      changeOnBlur={false}
      disabled={disabled || saving}
      aria-label={`${row.goodsName}排序`}
      title="数值越小越靠前"
      onChange={setSort}
      onBlur={handleBlur}
    />
  ) : (
    <Button
      size="small"
      type="text"
      style={{ padding: 0, height: "auto" }}
      disabled={disabled}
      aria-label={`编辑${row.goodsName}排序`}
      title="点击编辑，数值越小越靠前"
      onClick={handleEdit}
    >
      {row.sort} <EditOutlined />
    </Button>
  );
}
